/**
 * ExpectedSendersSection — Sources Spine Phase 2 PR 23.
 *
 * Sits on the domain Configuration tab. The customer declares
 * which known_providers should be sending mail as the domain. The
 * audit-job emits `vendor_drift_detected` action_items for any
 * Source not in the declared set, surfaced via the notification
 * bell from PR 22.
 *
 * Layout:
 *   - Searchable provider picker (typeahead with 100-result cap)
 *   - Inline list of currently declared vendors with remove buttons
 *   - Empty state explains the drift signal so first-time users
 *     understand WHY they'd curate a baseline
 *
 * The picker fetches /api/known-providers on each keystroke (debounced)
 * rather than loading the full catalog once — the provider catalog
 * can grow to hundreds of entries and a streamed search keeps the
 * payload tiny.
 */

import { useEffect, useRef, useState } from "react";
import {
  Plus,
  Loader2,
  X,
  ShieldCheck,
  Search,
} from "lucide-react";
import {
  api,
  type DomainDeclaredVendor,
  type KnownProvider,
} from "@/lib/api";
import { toast } from "@/lib/toast";

interface ExpectedSendersSectionProps {
  domainId: string;
}

const PICKER_SEARCH_DEBOUNCE_MS = 200;

export function ExpectedSendersSection({ domainId }: ExpectedSendersSectionProps) {
  const [declared, setDeclared] = useState<DomainDeclaredVendor[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getDomainDeclaredVendors(domainId)
      .then((r) => {
        if (!cancelled) setDeclared(r.declaredVendors);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [domainId]);

  async function handleDeclared(provider: KnownProvider) {
    // Optimistic insert; on failure, revert.
    const optimistic: DomainDeclaredVendor = {
      id: `optimistic-${provider.id}`,
      knownProviderId: provider.id,
      providerName: provider.name,
      providerCategory: provider.category,
      providerLogoUrl: provider.logoUrl,
      declaredBy: null,
      notes: null,
      declaredAt: new Date().toISOString(),
    };
    setDeclared((prev) => (prev ? [...prev, optimistic] : [optimistic]));
    try {
      const { declaredVendor } = await api.declareDomainVendor(domainId, {
        knownProviderId: provider.id,
      });
      if (declaredVendor) {
        setDeclared((prev) =>
          prev
            ? prev.map((v) => (v.id === optimistic.id ? declaredVendor : v))
            : [declaredVendor]
        );
      }
    } catch (err: unknown) {
      setDeclared((prev) => (prev ? prev.filter((v) => v.id !== optimistic.id) : prev));
      toast.error(err instanceof Error ? err.message : "Failed to declare vendor");
    }
    setPickerOpen(false);
  }

  async function handleUndeclare(vendor: DomainDeclaredVendor) {
    setDeclared((prev) => (prev ? prev.filter((v) => v.id !== vendor.id) : prev));
    try {
      await api.undeclareDomainVendor(domainId, vendor.id);
    } catch (err: unknown) {
      // Revert on failure — refetch to be safe (the optimistic local
      // state may have drifted from the server's truth).
      api.getDomainDeclaredVendors(domainId).then((r) => setDeclared(r.declaredVendors));
      toast.error(err instanceof Error ? err.message : "Failed to remove vendor");
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-3 py-2 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-700">
        Couldn't load expected senders: {error}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        Declare every provider that should send mail as this domain.
        The platform watches for drift — if a Source shows up that
        isn't in this list, you'll get an alert in your notifications
        so unexpected senders never slip through.
      </p>

      {declared && declared.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {declared.map((v) => (
            <li
              key={v.id}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full text-sm text-emerald-800"
            >
              {v.providerLogoUrl ? (
                <img
                  src={v.providerLogoUrl}
                  alt=""
                  className="w-4 h-4 rounded-sm object-contain"
                />
              ) : (
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              )}
              <span>{v.providerName}</span>
              <button
                type="button"
                onClick={() => handleUndeclare(v)}
                aria-label={`Remove ${v.providerName} from expected senders`}
                className="text-emerald-600 hover:text-emerald-900"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="px-3 py-4 bg-slate-50 border border-dashed border-slate-200 rounded-md text-sm text-slate-500">
          No expected senders declared yet. Add the providers you use
          (your ESP, marketing platform, CRM, etc.) so the platform
          can alert you to anything new.
        </div>
      )}

      <div>
        {pickerOpen ? (
          <ProviderPicker
            existingIds={new Set((declared ?? []).map((v) => v.knownProviderId))}
            onClose={() => setPickerOpen(false)}
            onPick={handleDeclared}
          />
        ) : (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
          >
            <Plus className="w-3.5 h-3.5" />
            Add expected sender
          </button>
        )}
      </div>
    </div>
  );
}

function ProviderPicker({
  existingIds,
  onClose,
  onPick,
}: {
  existingIds: Set<string>;
  onClose: () => void;
  onPick: (provider: KnownProvider) => void;
}) {
  const [query, setQuery] = useState("");
  const [providers, setProviders] = useState<KnownProvider[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number | null>(null);

  // Autofocus on mount so the user can start typing immediately.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Debounced provider search. The server caps results at 100 so even
  // an empty query (first open) renders fast.
  useEffect(() => {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setSearchLoading(true);
      api
        .getKnownProviders({ search: query.trim() || undefined })
        .then((r) => setProviders(r.providers))
        .catch(() => setProviders([]))
        .finally(() => setSearchLoading(false));
    }, PICKER_SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
  }, [query]);

  const visible = providers.filter((p) => !existingIds.has(p.id));

  return (
    <div className="border border-slate-200 rounded-md bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
        <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search providers (Mailchimp, SendGrid, Google Workspace…)"
          className="flex-1 text-sm bg-transparent focus:outline-none placeholder:text-slate-400"
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close picker"
          className="text-slate-400 hover:text-slate-700"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {searchLoading && providers.length === 0 ? (
          <div className="flex items-center justify-center py-6 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : visible.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-slate-500">
            {query.length > 0
              ? "No matching providers (or all matches already declared)."
              : "Every provider is already declared on this domain."}
          </div>
        ) : (
          <ul>
            {visible.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onPick(p)}
                  className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-slate-50"
                >
                  {p.logoUrl ? (
                    <img
                      src={p.logoUrl}
                      alt=""
                      className="w-5 h-5 rounded object-contain flex-shrink-0"
                    />
                  ) : (
                    <div
                      aria-hidden="true"
                      className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-500 flex-shrink-0"
                    >
                      {p.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-800 truncate">
                      {p.name}
                    </div>
                    <div className="text-xs text-slate-400 capitalize">
                      {p.category}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
